import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../store/hooks";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { CheckCircleOutlineOutlined, CloseOutlined } from "@mui/icons-material";
import { RootState } from "../../../../../../store/store";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
  IGetOptionsResponse,
} from "../../../../../../models/user/DynamicForm/DynamicForm";
import DynamicForm from "../../../../../../common/DynamicForm/DynamicForm";
import { CustomAction } from "../../../../../../store/customAction";
import { API_GET_LOOKUP_VALUES } from "../../../../../../Apis/ApiEndPoints";
import { PostAddDriver } from "../../../../../../store/user/policyChange/policyChangeActions";
import { IDriverPolicyChangeRequestParams } from "../../../../../../models/user/PolicyChange/PolicyChange";
import { formatDateToYYYYMMDD } from "../../../../../../utils/Utility";
import { useParams } from "react-router-dom";
import { GetAddDriverFormJson } from "../../../../../../store/user/driver/driverActions";
import MessageDialog from "../../../../../../components/MessageDialog/MessageDialog";
import { setAddDriverFormFieldsJson } from "../../../../../../store/user/driver/driverSlice";
import { Api } from "../../../../../..";
import { useTranslation } from "react-i18next";
import { IPolicyDriver } from "../../../../../../models/user/policyDetails/PolicyDetails";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  driver: IPolicyDriver;
}

const EditDriverForm: React.FC<IProps> = ({ open, setOpen, driver }) => {
  const dispatch = useAppDispatch();
  const { addDriverFormFieldsJson } = useAppSelector(
    (state: RootState) => state.driver
  );

  const { t } = useTranslation();

  const [formFieldsJson, setFormFieldsJson] =
    useState<IDynamicFormFieldsJson | null>(null);

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      getPrefixOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getPrefixOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getMaritalStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getMaritalStatusOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getGenderOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getGenderOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getRelationshipToInsuredOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getRelationshipToInsuredOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getEmploymentStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getEmployementStatusOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getEducationLevelOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getEducationLevelOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getLicensingStateOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getLicensingStateOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getDriverLicenseStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getDriverLicenseStatusOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
    }),
    [dispatch]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleEditDriverFormOnSubmit = () => {
    setMessageDialogOpen(true);
  };

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    handleClose();
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      dispatch(GetAddDriverFormJson());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (addDriverFormFieldsJson) {
      setFormFieldsJson(addDriverFormFieldsJson);
    }
  }, [addDriverFormFieldsJson]);

  useEffect(() => {
    if (addDriverFormFieldsJson && driver) {
      const updatedFormFieldsJson = {
        ...addDriverFormFieldsJson,
        sections:
          addDriverFormFieldsJson?.sections?.map((section) => ({
            ...section,
            fields: section?.fields?.map((field) => {
              const updatedField = { ...field };

              if (updatedField.name === "givenName") {
                updatedField.value = driver?.givenName;
              } else if (updatedField.name === "otherGivenName") {
                updatedField.value = driver?.otherGivenName;
              } else if (updatedField.name === "surName") {
                updatedField.value = driver?.surName;
              } else if (updatedField.name === "maritalStatus") {
                updatedField.value = driver?.maritalStatus?.toLowerCase();
              } else if (updatedField.name === "genderCd") {
                updatedField.value = driver?.genderCd?.toLowerCase();
              }
              // else if (updatedField.name === "relationshipToInsuredCd") {
              //   updatedField.value =
              //     driver?.relationshipToInsuredCd?.toLowerCase();
              // }
              else if (updatedField.name === "licenseNumber") {
                updatedField.value = driver?.licenseNumber;
              } else if (updatedField.name === "licensedStateProvCd") {
                updatedField.value = driver?.licensedStateProvCd;
              }
              // else if (updatedField.name === "dateOfBirth") {
              //   updatedField.value = driver?.dateOfBirth
              //     ? dayjs(driver?.dateOfBirth)
              //     : null;
              // }
              return updatedField;
            }),
          })) || [],
      };
      setFormFieldsJson(updatedFormFieldsJson);
    }
  }, [addDriverFormFieldsJson, driver]);

  return (
    <>
      <DialogContent>
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            asyncOptionsFunctions={asyncFunctions}
            onFormSubmit={handleEditDriverFormOnSubmit}
          />
        ) : (
          <></>
        )}
      </DialogContent>
      <Dialog open={messageDialogOpen} sx={{ backdropFilter: "blur(5px)" }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <IconButton onClick={handleMessageDialogClose}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              justifyContent: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <CheckCircleOutlineOutlined
              sx={{ fontSize: "60px", color: "green" }}
            />
            <Typography>{t("Policy Change has been initiated")}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditDriverForm;
